import * as React from 'react';
import { styled } from '@linaria/react';
import { Check } from 'lucide-react';

import useSound from '@/hooks/use-sound';

import DemoContext from '@/components/Demo/DemoContext';
import ButtonSecondary from '@/components/ButtonPlayful/ButtonSecondary';
import Spicy from '@/components/Spicy';

import { ExplodedState } from './BasicShapes.types';

function ExplodedExplanation({
  explodedState,
  handleResetExplosion,
}: {
  explodedState: ExplodedState;
  handleResetExplosion: () => void;
}) {
  const { setActiveControlId } = React.useContext(DemoContext);

  const [play] = useSound('/sounds/alarm-military.mp3', {
    volume: 0.1,
  });

  React.useEffect(() => {
    if (explodedState === 'exploded') {
      setActiveControlId(null);

      window.setTimeout(() => {
        play();
      }, 1000);
    }
  }, [explodedState, setActiveControlId, play]);

  if (explodedState === 'peace' || explodedState === 'aftermath') {
    return null;
  }

  return (
    <Wrapper style={{ opacity: explodedState === 'cleanup' ? 0 : 1 }}>
      <Content
        style={{ opacity: explodedState === 'cleanup' ? 0 : 1 }}
      >
        <Heading style={{ '--fade-duration': '2000ms' }}>
          Oh no! You blew up the line!
        </Heading>
        <Paragraph style={{ '--fade-duration': '2600ms' }}>
          By setting the start and end points to the same value,
          you've inadvertently discovered a{' '}
          <Spicy style={{ color: 'var(--wham-primary-500)' }}>
            logical paradox
          </Spicy>
          . Your device may explode momentarily.
        </Paragraph>
        <Paragraph style={{ '--fade-duration': '4500ms' }}>
          <strong>Just kidding!</strong> This doesn't cause any
          problems. You'll just wind up with an invisible line. 😄
        </Paragraph>
        <ResetButton
          data-color-mode="dark"
          Icon={Check}
          style={{ '--fade-duration': '5000ms' }}
          onClick={() => {
            handleResetExplosion();
          }}
        >
          Re-enable Demo
        </ResetButton>
      </Content>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  grid-area: code;
  align-self: end;
  position: relative;
  width: 100%;
  min-height: 100%;
  background: var(--color-background);
  padding: 16px;
  border-radius: 2px;
  outline: 1px solid var(--color-gray-400);
  transition: opacity 600ms 300ms;
  margin-top: -24px;
`;

const Content = styled.div`
  transition: opacity 500ms;
`;

const Heading = styled.p`
  font-size: 1.125rem;
  font-weight: var(--font-weight-bold);
  color: var(--wham-primary-500);
  text-align: center;
  margin-bottom: 1rem;
  animation: fadeFromTransparent 500ms var(--fade-duration, 0ms) both;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  color: var(--color-text);
  animation: fadeFromTransparent 500ms var(--fade-duration, 0ms) both;

  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const ResetButton = styled(ButtonSecondary)`
  margin-inline: auto;
  animation: fadeFromTransparent 500ms var(--fade-duration, 0ms) both;
`;

export default ExplodedExplanation;
